import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useRouter } from "next/router";
import Image from "next/legacy/image";
import { datadogRum } from "@datadog/browser-rum";

import {
  AppDispatch,
  selectSignupPersonalData,
  updateSignupPersonalData,
  useLazyValidateEmailInUseQuery,
  usePreferencesQuery,
} from "@chef/state-management";
import { Button, Checkbox, Input } from "@chef/components";
import {
  BASE_64_1X1_GREY,
  BRAND_PATHS,
  CURRENCY,
  getDishCount,
  getPreviewRecipes,
  NEW_PASSWORD_MIN_LENGTH,
  PREFERENCE_TYPE_IDS,
} from "@chef/constants";
import { useEffectOnce, useExtendedForm } from "@chef/hooks";
import { AppShell } from "@chef/feature-app-shell";
import { FlexWrapper } from "@chef/smart-components";
import { BlockContent, ISection } from "@chef/feature-sanity";
import { getAttribute } from "@chef/state-management/helpers";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { useTrack } from "@chef/state-management/hooks";

import { Header } from "../components/Header";
import { useSelectedProductAndVariationDetails } from "../hooks/useSelectedProductAndVariationDetails";
import { CustomerExistsModal } from "../components/CustomerExistsModal";
import { useTrackingData } from "../hooks/useTrackingData";
import { useSignupSession } from "../hooks/useSignupSession";

import { intl } from "./SignupCreateLoginDetails.Intl";

interface IFormInput {
  email: string;
  password: string;
  allowEmailAdvertisment: boolean;
}
type ErrorResponse = { data: { redirectUri: string } };

interface SignupCreateLoginDetailsProps {
  sanityContent?: ISection | null;
}

const schema = yup
  .object({
    email: yup
      .string()
      .email(intl.EMAIL_ERROR_MESSAGE)
      .required(intl.EMAIL_REQUIRED_MESSAGE),

    password: yup
      .string()
      .min(NEW_PASSWORD_MIN_LENGTH, intl.PASSWORD_ERROR_MESSAGE)
      .required(intl.PASSWORD_REQUIRED_MESSAGE),

    allowEmailAdvertisment: yup.boolean(),
  })
  .required();

export const SignupCreateLoginDetails = ({
  sanityContent,
}: SignupCreateLoginDetailsProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const [emailInUse, setEmailInUse] = useState(false);

  const { signupSession, updateSignupSession } = useSignupSession();

  const { data: preferenceQuery } = usePreferencesQuery();

  const { email, password, allowEmailAdvertisment } = useSelector(
    selectSignupPersonalData,
  );

  const track = useTrack();

  const router = useRouter();

  const [validateEmailInUse, { isLoading: isValidatingEmail }] =
    useLazyValidateEmailInUseQuery();

  const { register, handleSubmit, formValues, setValue } =
    useExtendedForm<IFormInput>({
      defaultValues: {
        // not needed, but if the user refreshes the page we want to keep these
        email,
        password,
        allowEmailAdvertisment,
      },
      resolver: yupResolver(schema),
      mode: "all",
    });

  const trackingData = useTrackingData();

  const onSubmit: SubmitHandler<IFormInput> = async ({
    email,
    password,
    allowEmailAdvertisment,
  }) => {
    try {
      await validateEmailInUse({ email }).unwrap();

      dispatch(
        updateSignupPersonalData({
          email,
          password,
          allowEmailAdvertisment,
        }),
      );

      track("accountCreated", {
        affiliation: "Frontend process",
        currency: CURRENCY,
        coupon: trackingData.coupon,
        discount: trackingData.discount,
        products: trackingData.products,
      });

      if (allowEmailAdvertisment) {
        track("leadGenerated", {
          affiliation: "Frontend process",
          mail: email,
        });
      }

      await updateSignupSession({
        auth: {
          password,
        },
        subject: {
          email,
        },
      }).unwrap();

      router.push(BRAND_PATHS.SIGNUP_DELIVERY_DETAILS_HREF);
    } catch (error) {
      const err = error as ErrorResponse;
      if (err.data.redirectUri) {
        window.location.href = err.data.redirectUri;
        return;
      }
      setEmailInUse(true);
    }
  };

  useEffectOnce(() => {
    if (signupSession?.reference) {
      datadogRum.addRumGlobalContext("reference", signupSession.reference);
    }
  }, !!signupSession);

  const { selectedVariation } =
    useSelectedProductAndVariationDetails(signupSession);

  const selectedPortion =
    (selectedVariation && getAttribute("Portions", selectedVariation)) || 0;

  const selectedMeals =
    (selectedVariation && getAttribute("Meals", selectedVariation)) || 0;

  const week = signupSession?.basket?._meta?.week;
  const year = signupSession?.basket?._meta?.year;
  const selectedPreferenceIds = signupSession?.preferencesIds
    ?.filter((id) => id !== undefined)
    ?.map((id) => id);

  const selectedConceptPreferenceIds = preferenceQuery?.preferences
    .filter(
      (p) =>
        selectedPreferenceIds?.includes(p.preferenceId) &&
        p.preferenceTypeId === PREFERENCE_TYPE_IDS.CONCEPT,
    )
    .map((p) => p.preferenceId);

  return (
    <AppShell title={intl.CREATE_ACCOUNT}>
      <AppShell.Meta />

      <Header step={2} />
      <AppShell.DiscountBanner cta="text" />

      <AppShell.Main grid>
        <RecipesPreview
          className="md:hidden"
          preferenceIds={selectedConceptPreferenceIds}
          week={week}
          year={year}
          portions={selectedPortion}
        />
        <FlexWrapper
          back={{ type: "hidden" }}
          close={{ type: "hidden" }}
          className="md:mt-10"
          background="white"
        >
          <div className="relative px-4 py-8 md:p-10" id="login-details-card">
            <div className="absolute top-0 right-0">
              <Image
                src="https://ggfrontendassets.azureedge.net/publicassets/veggies.png"
                layout="intrinsic"
                width={135}
                height={103}
                objectFit="cover"
                alt=""
              />
            </div>

            <h2 className="pb-8 text-xl md:text-2xl">
              <strong>{intl.CREATE_ACCOUNT}</strong>
            </h2>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
            >
              <Input
                {...register("email")}
                type="email"
                autoComplete="email"
                placeholder={intl.EMAIL_PLACEHOLDER_TEXT}
                label={intl.EMAIL_PLACEHOLDER_TEXT}
                id="new-email"
              />

              <Input
                {...register("password")}
                placeholder={intl.PASSWORD_PLACEHOLDER_TEXT}
                label={intl.PASSWORD_PLACEHOLDER_TEXT}
                id="new-password"
                autoComplete="new-password"
                type="password"
              />

              <Checkbox
                {...register("allowEmailAdvertisment")}
                id="advertisment-consent-checkbox"
              >
                <span className="text-xs">{intl.CHECKBOX_TEXT}</span>
              </Checkbox>

              <Button
                primary
                className="w-full"
                type="submit"
                id="go-to-step-3-button"
                loading={isValidatingEmail}
              >
                {intl.SUBMIT_BUTTON}
              </Button>
            </form>
          </div>
        </FlexWrapper>
        {sanityContent && (
          <FlexWrapper
            back={{ type: "hidden" }}
            close={{ type: "hidden" }}
            background="white"
          >
            <BlockContent
              body={sanityContent.content.body}
              prose={{ disabled: true }}
            />
          </FlexWrapper>
        )}
        <FlexWrapper
          back={{ type: "hidden" }}
          close={{ type: "hidden" }}
          className="shadow-none"
        >
          <div
            className="hidden text-center rounded-tl rounded-tr bg-informationBG md:block"
            id="order-details-card"
          >
            <div className="pt-4 mb-1 text-base">
              <strong>{intl.YOU_HAVE_SELECTED}</strong>
            </div>

            <p className="pb-4 text-sm">
              {selectedMeals} {intl.DINNERS_FOR} {selectedPortion}{" "}
              {intl.AMOUNT_OF_PEOPLE}
            </p>
          </div>
        </FlexWrapper>
      </AppShell.Main>

      <CustomerExistsModal
        initalEmail={formValues.email}
        initialLoginMethod="email"
        isOpen={emailInUse}
        onClose={() => {
          setEmailInUse(false);
          setValue("email", "");
        }}
      />
      <AppShell.TabBar />
    </AppShell>
  );
};

interface RecipesPreviewProps {
  preferenceIds: string[] | undefined;
  week: number | undefined;
  year: number | undefined;
  portions: string | number;
  className?: string;
}

const RecipesPreview = ({
  preferenceIds,
  week,
  year,
  portions,
  className,
}: RecipesPreviewProps) => {
  if (!week || !year) {
    return null;
  }

  const recipes = preferenceIds?.flatMap((preferenceId) =>
    getPreviewRecipes({ week, year, preferenceId }),
  );

  const dishCount = getDishCount({ week, year, portions: +portions });

  return (
    <FlexWrapper
      back={{ type: "hidden" }}
      close={{ type: "hidden" }}
      className={className}
      background="white"
    >
      <div className="flex flex-col gap-3 px-4 py-6">
        <p className="text-sm text-center">
          {intl.YOUR_ACCOUNT_GIVES_ACCESS_TO(dishCount)}
        </p>
        <div className="flex justify-center gap-1">
          {recipes?.slice(0, 5).map((recipe) => (
            <div className="relative w-14 h-14" key={recipe.recipeId}>
              <Image
                src={findFeaturedPortraitImage(recipe)?.url || BASE_64_1X1_GREY}
                width={56}
                height={56}
                alt={recipe.recipeName}
                className="object-cover rounded"
                sizes="60px"
              />
            </div>
          ))}
        </div>
      </div>
    </FlexWrapper>
  );
};
